import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PatreonNav from "../components/PatreonNav";
import PatreonFooter from "../components/PatreonFooter";

interface Feature {
  icon: string;
  title: string;
  description: string;
  color: string;
}

interface FeatureCategory {
  title: string;
  description: string;
  features: Feature[];
}

const FeaturesPage = () => {
  const categories: FeatureCategory[] = [
    {
      title: "Monetization Tools",
      description: "Multiple ways to earn from your creative work",
      features: [
        {
          icon: "💰",
          title: "Membership Tiers",
          description: "Create multiple membership levels with different benefits and pricing",
          color: "#ff424d"
        },
        {
          icon: "🎁",
          title: "Digital Downloads",
          description: "Sell digital products and exclusive content directly to your patrons",
          color: "#5fc1f1"
        },
        {
          icon: "🛍️",
          title: "Merchandise",
          description: "Integrate your online store and sell physical products",
          color: "#f15ff1"
        },
        {
          icon: "💳",
          title: "One-time Payments",
          description: "Accept tips and one-time contributions from supporters",
          color: "#00bda5"
        }
      ]
    },
    {
      title: "Content Creation",
      description: "Powerful tools to create and share your work",
      features: [
        {
          icon: "✍️",
          title: "Rich Text Editor",
          description: "Create beautiful posts with images, videos, audio, and formatting",
          color: "#ff424d"
        },
        {
          icon: "🎥",
          title: "Video Hosting",
          description: "Upload and stream HD video content directly to your patrons",
          color: "#5fc1f1"
        },
        {
          icon: "🎙️",
          title: "Podcast Integration",
          description: "Distribute exclusive podcast episodes to your members",
          color: "#f15ff1"
        },
        {
          icon: "📊",
          title: "Polls & Surveys",
          description: "Engage your community with interactive polls and surveys",
          color: "#00bda5"
        }
      ]
    },
    {
      title: "Community Engagement",
      description: "Build meaningful connections with your supporters",
      features: [
        {
          icon: "💬",
          title: "Comments & Discussion",
          description: "Foster conversations with threaded comments and reactions",
          color: "#ff424d"
        },
        {
          icon: "📧",
          title: "Direct Messaging",
          description: "Communicate privately with individual patrons or groups",
          color: "#5fc1f1"
        },
        {
          icon: "🎯",
          title: "Exclusive Content",
          description: "Lock content behind specific membership tiers",
          color: "#f15ff1"
        },
        {
          icon: "🏆",
          title: "Patron Recognition",
          description: "Highlight and celebrate your most supportive members",
          color: "#00bda5"
        }
      ]
    },
    {
      title: "Analytics & Insights",
      description: "Understand and grow your community",
      features: [
        {
          icon: "📈",
          title: "Revenue Analytics",
          description: "Track earnings, growth trends, and financial projections",
          color: "#ff424d"
        },
        {
          icon: "👥",
          title: "Audience Insights",
          description: "Understand your patron demographics and behavior",
          color: "#5fc1f1"
        },
        {
          icon: "📊",
          title: "Content Performance",
          description: "See which posts drive the most engagement",
          color: "#f15ff1"
        },
        {
          icon: "🎯",
          title: "Goal Tracking",
          description: "Set and monitor progress toward financial milestones",
          color: "#00bda5"
        }
      ]
    },
    {
      title: "Business Tools",
      description: "Professional features for serious creators",
      features: [
        {
          icon: "🧾",
          title: "Tax Support",
          description: "Automated tax forms and reporting for US creators",
          color: "#ff424d"
        },
        {
          icon: "💵",
          title: "Flexible Payouts",
          description: "Get paid monthly via direct deposit or PayPal",
          color: "#5fc1f1"
        },
        {
          icon: "🔐",
          title: "Team Management",
          description: "Add team members with specific roles and permissions",
          color: "#f15ff1"
        },
        {
          icon: "🔌",
          title: "API Access",
          description: "Integrate with third-party tools and custom applications",
          color: "#00bda5"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Features | Patreon</title>
        <meta name="description" content="Explore Patreon features for memberships, shops, community, messaging, and analytics." />
        <link rel="canonical" href={window.location.origin + "/features"} />
        <meta property="og:title" content="Features | Patreon" />
        <meta property="og:description" content="Everything you need to succeed as a creator" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Features | Patreon" />
        <meta name="twitter:description" content="Everything you need to succeed as a creator" />
      </Helmet>

      <PatreonNav />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            className="text-6xl md:text-7xl font-bold mb-6"
            style={{ fontFamily: "var(--patreon-font-primary)" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Everything you need to{" "}
            <span style={{ color: "var(--patreon-coral)" }}>succeed</span>
          </motion.h1>
          
          <motion.p
            className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Powerful tools designed to help creators build sustainable businesses and meaningful communities.
          </motion.p>
        </div>
      </section>

      {/* Features Grid */}
      {categories.map((category, categoryIndex) => (
        <section key={categoryIndex} className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4">{category.title}</h2>
              <p className="text-xl text-gray-600">{category.description}</p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {category.features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-shadow"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                >
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-4"
                    style={{ backgroundColor: `${feature.color}15` }}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Platform Benefits */}
      <section className="py-24 px-4 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Why creators choose us
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                stat: "0%",
                label: "Platform fee on Pro & Premium",
                description: "Keep more of what you earn with our creator-friendly pricing"
              },
              {
                stat: "200k+",
                label: "Active creators",
                description: "Join a thriving community of successful creators worldwide"
              },
              {
                stat: "$3B+",
                label: "Paid to creators",
                description: "We've helped creators earn billions doing what they love"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
              >
                <div className="text-6xl font-bold mb-2" style={{ color: "var(--patreon-coral)" }}>
                  {benefit.stat}
                </div>
                <div className="text-2xl font-semibold mb-3">{benefit.label}</div>
                <p className="text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Start creating today
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of creators building their dream careers with our platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-8 py-4 rounded-full text-white font-semibold text-lg hover:opacity-90 transition-all"
              style={{ backgroundColor: "var(--patreon-coral)" }}
            >
              Get Started for Free
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 rounded-full font-semibold text-lg border-2 hover:bg-gray-50 transition-all"
              style={{ borderColor: "var(--patreon-coral)", color: "var(--patreon-coral)" }}
            >
              View Pricing
            </Link>
          </div>
        </motion.div>
      </section>

      <PatreonFooter />
    </div>
  );
};

export default FeaturesPage;
